import * as ko from 'knockout'
import { v4 } from 'uuid'
import { BetInformers } from '@core/Areas/Racing/Components/BetInformers/BetInformers'
import { RacePanels } from '@core/Areas/Racing/Components/Panels'
import { RaceStatus } from '@core/Areas/Racing/Types'
import { Disposable } from '../../../../AppUtils/Framework/Disposable/Disposable'
import Guard from '../../../../AppUtils/Framework/Guard'
import * as EventAggregator from '../../../../AppUtils/Framework/Messaging/EventAggregator'
import { state$ as bettingV2CommandDriver$ } from '../../../Components/Commands/driver'
import * as FormGiddyUpRaceInformation from '../../../Components/Form/FormGiddyUpRaceInformation'
import type { IObservableFinisher } from '../../../Model/Observables/IObservableFinisher'
import ObservableResultsPage from '../../../Model/Observables/ObservableResultsPage'

type ViewName = 'View Field' | 'View Results'

export class ResultsPageViewModel extends Disposable {
  public showProtest: ko.PureComputed<boolean>
  public raceInformation: ObservableResultsPage
  public simplePlacings: ko.Observable<string>
  public showField: ko.PureComputed<boolean>
  public isRaceClosed: ko.PureComputed<boolean>
  public viewName: ko.Observable<ViewName>
  public isRaceReplayOn: ko.Observable<boolean>
  public hasGiddyUpRaceKey: ko.PureComputed<boolean>
  public raceReplayId: string
  public BetInformers = BetInformers
  public RacePanels = RacePanels
  private ldSubscription!: Rx.IDisposable
  private displayTypeSubscription!: Rx.IDisposable
  public fieldVisibleState!: ko.Observable<boolean>
  public status!: ko.PureComputed<RaceStatus>
  public finishers!: ko.ObservableArray<IObservableFinisher>
  public hasVideoReplay!: ko.PureComputed<boolean>

  constructor(params: { raceInformation: ObservableResultsPage }) {
    super()
    Guard.notNull(params)
    Guard.notNull(params.raceInformation)

    this.raceReplayId = `id-${v4()}`

    this.raceInformation = params.raceInformation

    this.simplePlacings = this.raceInformation.simplePlacings as ko.Observable<string>
    this.viewName = ko.observable<ViewName>('View Field')
    this.showProtest = ko.pureComputed<boolean>(() => {
      const protestStatus = this.raceInformation.protestStatus()

      return protestStatus !== undefined && protestStatus.length > 0
    })
    this.isRaceClosed = ko.pureComputed<boolean>(() => {
      return this.status() === RaceStatus.Closed
    })
    this.showField = ko.pureComputed<boolean>(() => {
      return this.isRaceClosed() || this.fieldVisibleState()
    })
    this.status = ko.pureComputed<RaceStatus>(() => {
      return this.raceInformation.raceStatus() as RaceStatus
    })

    this.isRaceReplayOn = ko.observable(false)

    this.hasVideoReplay = ko.pureComputed<boolean>(
      () =>
        this.raceInformation.raceReplay.hasVideo() &&
        [RaceStatus.Released, RaceStatus.Interim].includes(this.status())
    )

    this.fieldVisibleState = ko.observable(false)

    this.registerDisposals(() => {
      this.showProtest.dispose()
      this.isRaceClosed.dispose()
      this.showField.dispose()
    })

    this.registerHandlers()

    this.hasGiddyUpRaceKey = ko.pureComputed<boolean>(() => !!this.raceInformation.giddyUpRaceKey())

    this.displayTypeSubscription = bettingV2CommandDriver$
      .map(record => {
        return {
          fieldVisible: record.fieldVisible,
        }
      })
      .distinctUntilChanged()
      .subscribe(result => {
        this.fieldVisibleState(result.fieldVisible)
      })

    this.registerDisposals(() => {
      this.ldSubscription?.dispose()
      this.displayTypeSubscription?.dispose()
    })
  }

  private registerHandlers() {
    if (this.raceInformation.raceReplay.hasVideo()) {
      this.subscribeToRaceReplay(this.raceReplayId)
    }
  }

  private subscribeToRaceReplay(id: string): void {
    this.safeSubscribe(`race-replay-started-for-${id}`, () => {
      this.isRaceReplayOn(true)
    })

    this.safeSubscribe(`race-replay-stopped-for-${id}`, () => {
      this.isRaceReplayOn(false)
    })
  }

  public changeView() {
    EventAggregator.publish(`stop-all-race-replay`)

    if (this.viewName() === 'View Results') {
      this.viewName('View Field')
    } else {
      this.viewName('View Results')
    }
  }

  public toggleRaceReplay() {
    EventAggregator.publish(`toggle-race-replay-for-${this.raceReplayId}`)
  }

  public openGiddyUp() {
    FormGiddyUpRaceInformation.openGiddyUp(
      this.raceInformation.giddyUpRaceKey() as string,
      this.raceInformation.giddyUpFixtureKey() as string,
      finisherNumber => {
        return this.raceInformation.raceFinishers.getFinisher(finisherNumber)
      },
      () => {
        return {
          raceCourse: this.raceInformation.meetingInformation.meetingName(),
          raceNumber: this.raceInformation.raceNumber(),
          distance: this.raceInformation.meetingInformation.selectedRace.distance(),
          trackCondition: null,
          weatherClass: null,
          raceStatus: this.raceInformation.raceStatus(),
          raceTimeLocal: null,
          raceResults: this.simplePlacings(),
        }
      },
      // placeSelection is n/a for results,
      () => {
        //
      },
      () => {
        //
      },
      () => {
        //
      }
    )
  }
}
